import React, {forwardRef} from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {SIZES_TYPES, Sizes} from './constants';
import iconStyles from './icon.component.module.styl';
import MultiColor, {ICONS_SVG} from './icons-multi-color-generated';

import type {IconSize} from './constants';
import type {CSSProperties, ComponentPropsWithoutRef} from 'react';


const classNames = withCssModule(iconStyles);

const styles = (size?: IconSize, style?: CSSProperties) => (
    size
        ? {
                fontSize: SIZES_TYPES[size] || undefined,
                ...style,
            }
        : style
);

const customStyles = (size?: IconSize, style?: CSSProperties): {
    fontSize: React.CSSProperties['fontSize'],
    fontWeight: React.CSSProperties['fontWeight'],
} => ({
    fontSize: (size ? SIZES_TYPES[size] : SIZES_TYPES[Sizes.Size_16]) || undefined,
    fontWeight: 400,
    ...style,
});


// eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
const getNativeProps = ({size, type, mix, unit, block, fixedBox, loader, rootProps,
    ...nativeProps
}: IconProps): NativeIconProps => nativeProps;

type NativeIconProps = ComponentPropsWithoutRef<'i'>;

export interface IconProps extends NativeIconProps {
    /**
     * Name of the icon to render. Pass any value from the `Icons` object (which
     * merges all single-color font-glyph icons and multi-color SVG icons into one
     * lookup). The component automatically detects whether the name belongs to the
     * multi-color set and switches rendering between an `<i>` font glyph and an
     * inline SVG accordingly.
     *
     * @example
     * ```tsx
     * import {Icons} from '@pb/kit-component-icon';
     * <Icon type={Icons.SEARCH} />         // single-color font glyph
     * <Icon type={Icons.EXCEL} />          // multi-color inline SVG
     * ```
     */
    type: string,
    /**
     * Unit style variant
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     * */
    unit?: boolean,

    /**
     * Size token controlling the icon's `font-size` (and therefore the SVG
     * bounding box for multi-color icons). Accepts any key from the `Sizes`
     * object (e.g. `Sizes.Size_16`). The value is converted from pixels to `rem`
     * at render time so it scales with the user's base font-size preference.
     *
     * When omitted, no explicit `font-size` is applied and the icon inherits its
     * size from the surrounding CSS — useful when the parent already sets the
     * correct `font-size`.
     */
    size?: IconSize,

    /**
     * CSS rule `display: block`
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    block?: boolean,

    /**
     * Show icon in block 14 x 14px
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    fixedBox?: boolean,

    /**
     * Icon with loader
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    loader?: boolean,

    /**
     * Custom classes
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,
    /**
     * Extra props for the root HTML element
     * @deprecated It will be removed in 6 months 09-15-2025. pass native props directly
     * */
    rootProps?: NativeIconProps,

    /**
     * Ref to the HTML element
     */
    ref?: React.Ref<HTMLElement>,
}

const MultiColorValues = Object.fromEntries(
    Object
        .values(MultiColor)
        .map(value => [value, true]),
);

function isMultiColorIcon(type: string): type is keyof typeof ICONS_SVG {
    return MultiColorValues[type];
}

/**
 * Renders a single icon from the PitchBook icon set — either a single-color
 * icon font glyph or a multi-color inline SVG.
 *
 * Pass an icon name from `SingleColor` or `MultiColor` enums as the `type`
 * prop. The component automatically detects which rendering path to use: font
 * glyphs are rendered as an `<i>` element; multi-color icons are rendered as an
 * SVG component. The `size` prop controls `font-size` using design-system tokens.
 *
 * By default, `aria-hidden="true"` is applied so screen readers ignore
 * decorative icons. Override this for meaningful icons and pair with an
 * `aria-label` on the parent element.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/fundamentals-icons--documentation}
 */
export const Icon = forwardRef<HTMLElement, IconProps>(function Icon(props, ref) {
    const {
        type,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        unit,
        size,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        block,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        fixedBox,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        loader,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        mix,
        className,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        rootProps,
    } = props;

    const isMultiColor = isMultiColorIcon(type);

    const nativeProps: NativeIconProps = {
        ...rootProps,
        ...getNativeProps(props),
        'aria-hidden': props['aria-hidden'] ?? true,
        'className': classNames(
            mix,
            className,
            rootProps?.className,
            {
                ['icon-' + type]: !isMultiColor,
                ['multicolor-icon-' + type]: isMultiColor,
                'icon_wrapper': fixedBox,
                'icon_unit': unit,
                'icon_block': block,
                'icon_with-loader': loader,
            },
        ),
    };


    if (isMultiColor) {
        const Svg = ICONS_SVG[type] as React.FC<NativeIconProps & {ref?: React.Ref<HTMLElement>}>;
        return (
            <Svg
                {...nativeProps}
                style={customStyles(size, props.style)}
                ref={ref}
            />
        );
    } else {
        return <i {...nativeProps} style={styles(size, props.style)} ref={ref}/>;
    }
});

Icon.displayName = 'Icon';
